import { useState } from "react";
import { useDateFormatter, useTranslations } from "../../contexts/Translation";
import { useRole } from "../../contexts/UserProvider";
import { useOrgGoal } from "../../contexts/OrganizationGoal";
import SystemMessage from "../../components/SystemMessage/Index";
import Section from "../Section";
import OrgGoalForm from "../Forms/OrgGoalForm";
import GoalConclusionForm from "../Forms/GoalConclusionForm";
import Comments from "../Comments/List";
import { categoryLabel } from "../Dashboard/labels";
import ConclusionFlag from "../ImprovementGoal/ConclusionFlag";
import GoalHero from "../ImprovementGoal/GoalHero";
import GoalPhases from "../ImprovementGoal/GoalPhases";
import SurveyTable from "../ImprovementGoal/SurveyTable";
import { CoachCard, NextSurveyCard, SuccessCard } from "../ImprovementGoal/GoalAside";
import AiCoach from "../AiCoach/AiCoach";
import { goalPhases } from "../ImprovementGoal/phases";
import { orgRows } from "../ImprovementGoal/goalSurveys";
import { CreateImprovementGoalLink } from "./CreateImprovementGoalLink";
import styles from "../ImprovementGoal/Details.module.css";

const OrgGoalDetails = () => {
  const t = useTranslations("improvementGoals");
  const d = useDateFormatter();
  const { goal, recentStats } = useOrgGoal();
  const role = useRole();
  const [isEdit, setIsEdit] = useState(false);
  const [journalKey, setJournalKey] = useState(0);

  if (!goal) {
    return <SystemMessage message='loading' />;
  }

  const isOwner = !!goal.isOwner;
  const categories = goal.categories.filter(Boolean);
  const { phases, progress, conclusionOpen } = goalPhases(goal);
  const rows = orgRows({
    goal,
    recentStats,
    labels: {
      baseline: t("orgOriginalResultsFromTo", d(goal.originalFrom), d(goal.originalTo)),
      recent: t("orgResultsFromTo", d(recentStats?.total.startTime), d(recentStats?.total.endTime)),
    },
  });

  return (
    <>
      <Section>
        <GoalHero
          goal={goal}
          backTo='/organization'
          backLabel={t("backToOrganization")}
          facts={
            <>
              <li>{t("orgOriginalResultsFromTo", d(goal.originalFrom), d(goal.originalTo))}</li>
              {goal.registered && <li>{t("startLabel", d(goal.registered))}</li>}
              <li>{t("endLabel", d(goal.endDate))}</li>
            </>
          }
          isOwner={!!goal.canEdit}
          isEdit={isEdit}
          onToggleEdit={() => setIsEdit(!isEdit)}
        />
        <GoalPhases phases={phases} progress={progress} conclusionOpen={conclusionOpen} />
      </Section>
      <Section span={8} className={styles.main}>
        {isEdit ? (
          <div className={styles.card}>
            <OrgGoalForm isEdit onSuccess={() => setIsEdit(false)} />
          </div>
        ) : (
          <div className={styles.details}>
            <section className={styles.card}>
              <h2 className={styles.cardHeading}>{t("goalCardHeading")}</h2>
              <p className={styles.preWrap}>{goal.goal}</p>
              <p className={styles.text}>
                <strong>{t("improvementCategories")}: </strong>
                {categories.length > 0 ? categories.map((c) => t(categoryLabel(c))).join(", ") : t("noSelectedCategories")}
              </p>
            </section>
            <section className={styles.card}>
              <h2 className={styles.cardHeading}>{t("situationCardHeading")}</h2>
              <p className={styles.preWrap}>{goal.situation}</p>
            </section>
            <section className={styles.card}>
              <h2 className={styles.cardHeading}>{t("planCardHeading")}</h2>
              <p className={styles.preWrap}>{goal.plan}</p>
            </section>
          </div>
        )}

        <SurveyTable rows={rows} categories={categories} linkRows={false} />

        <section className={styles.card}>
          {conclusionOpen && isEdit ? (
            <GoalConclusionForm orgGoal={goal} onSuccess={() => setIsEdit(false)} />
          ) : (
            <>
              <h2 className={styles.cardHeading}>
                {t("conclusionHeading")}
                {conclusionOpen && <ConclusionFlag isSuccess={goal.isSuccess} successLabel={t("success")} failureLabel={t("fail")} />}
              </h2>
              <p className={styles.preWrap}>{conclusionOpen ? goal.result || t("emptyConclusion") : t("patienceConclusion")}</p>
            </>
          )}
          {conclusionOpen && ["supporter", "owner"].includes(`${role}`) && <CreateImprovementGoalLink from={goal.endDate} />}
        </section>

        <section className={styles.card}>
          <h2 className={styles.cardHeading}>{t("journalHeading")}</h2>
          <Comments key={journalKey} orgGoalId={goal.id} />
        </section>
      </Section>
      <Section span={4}>
        <aside>
          <CoachCard
            isOwner={isOwner}
            coach={
              <AiCoach
                coachingType='orgGoalComment'
                orgGoalId={goal.id}
                variant='onDark'
                buttonLabel={t("askCoach")}
                onComplete={() => setJournalKey((k) => k + 1)}
              />
            }
          />
          <NextSurveyCard />
          <SuccessCard />
        </aside>
      </Section>
    </>
  );
};

export default OrgGoalDetails;
